import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

const GOLD     = "#C9A96E";
const INK      = "#1C1915";
const INK_SOFT = "#4A4540";
const BORDER   = "#E7E1D8";
const SOFT_BG  = "#F7F5F2";
const WHITE    = "#FFFFFF";

const inputStyle = {
  width: "100%", padding: "9px 11px",
  border: `1px solid ${BORDER}`, borderRadius: 7,
  fontSize: 13, color: INK, background: "#FAFAF8",
  boxSizing: "border-box", outline: "none",
};
const taStyle = {
  ...inputStyle, resize: "vertical", minHeight: 90, fontFamily: "inherit",
};
const labelStyle = {
  display: "block", fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600,
};
const sectionTitle = {
  fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase",
  color: GOLD, marginBottom: 14, fontWeight: 700,
};
const card = {
  background: WHITE, border: `1px solid ${BORDER}`,
  borderRadius: 12, padding: "22px 24px", marginBottom: 20,
};

const STATUT_COLORS = {
  brouillon: { bg: SOFT_BG,    color: INK_SOFT,  label: "Brouillon"              },
  soumis:    { bg: "#EEF4FD",  color: "#2E6DB4", label: "En attente validation"  },
  valide:    { bg: "#E8F6EF",  color: "#1A7A44", label: "Validé"                 },
  rejete:    { bg: "#FDEDEC",  color: "#C0392B", label: "Rejeté"                 },
};

const FORM_INIT = {
  projet_id: "", date_reunion: "", lieu: "", presents: "",
  avancement_global: 0, etapes_realisees: "", etapes_a_venir: "",
  observations: "", mises_en_demeure: "", photos: [],
};

export default function PVChantierPage({ profile, isOPCMandate = false, cabinetId = null }) {
  void isOPCMandate;
  const [pvList,    setPvList]    = useState([]);
  const [projets,   setProjets]   = useState([]);
  const [vue,       setVue]       = useState("liste");
  const [pvSelec,   setPvSelec]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [msg,       setMsg]       = useState("");
  const [form,      setForm]      = useState(FORM_INIT);

  const isAdmin = profile?.role === "admin";

  useEffect(() => { if (cabinetId == null) return; fetchProjets(); fetchPV(); }, [cabinetId]);

  async function fetchProjets() {
    const { data } = await supabase.from("projects").select("id, name").eq("cabinet_id", cabinetId).order("name");
    if (data) setProjets(data);
  }

  async function fetchPV() {
    let q = supabase.from("pv_chantier").select(`
      *, 
      auteur:auteur_id(full_name, email),
      projet:projet_id(name)
    `).eq("cabinet_id", cabinetId).order("created_at", { ascending: false });
    if (!isAdmin) q = q.eq("auteur_id", profile.id);
    const { data } = await q;
    if (data) setPvList(data);
  }

  function updateForm(field, val) {
    setForm(f => ({ ...f, [field]: val }));
  }

  async function handlePhotos(e) {
    const files = Array.from(e.target.files);
    const base64s = await Promise.all(files.map(f => new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result);
      reader.onerror = rej;
      reader.readAsDataURL(f);
    })));
    setForm(prev => ({ ...prev, photos: [...(prev.photos || []), ...base64s] }));
  }

  function supprimerPhoto(i) {
    setForm(prev => ({ ...prev, photos: prev.photos.filter((_, j) => j !== i) }));
  }

  async function handleSauvegarder(statut = "brouillon") {
    setLoading(true);
    setMsg("");
    const { error } = await supabase.from("pv_chantier").insert({
      ...form,
      cabinet_id: cabinetId,
      auteur_id: profile.id,
      statut,
    });
    if (error) { setMsg("Erreur : " + error.message); }
    else {
      setMsg(statut === "soumis" ? "PV soumis à l'administrateur ✓" : "Brouillon sauvegardé ✓");
      if (statut === "soumis") await envoyerNotifAdmin();
      fetchPV();
      setTimeout(() => setVue("liste"), 1500);
    }
    setLoading(false);
  }

  async function envoyerNotifAdmin() {
    const { data: admins } = await supabase
      .from("profiles").select("id").eq("role", "admin").eq("cabinet_id", cabinetId);
    if (!admins) return;
    const notifs = admins.map(a => ({
      destinataire_id: a.id,
      expediteur_id: profile.id,
      type: "pv_soumis",
      titre: "Nouveau PV de chantier à valider",
      message: `${profile.full_name || profile.email} a soumis un PV de réunion.`,
      lien_type: "pv_chantier",
    }));
    await supabase.from("notifications").insert(notifs.map((n) => ({ ...n, cabinet_id: cabinetId })));
  }

  async function genererRapportIA(pv) {
    setIaLoading(true);
    setMsg("");
    try {
      const prompt = `Tu es un expert en coordination et pilotage de projets immobiliers haut de gamme au Maroc.
Tu dois reformuler les notes brutes d'un PV de réunion de chantier en un rapport professionnel, structuré et clair, destiné à être validé par le directeur de projet avant envoi au client.

Voici les informations brutes :
- Date de réunion : ${pv.date_reunion}
- Lieu : ${pv.lieu || "non précisé"}
- Personnes présentes : ${pv.presents || "non précisé"}
- Avancement global : ${pv.avancement_global}%
- Étapes réalisées : ${pv.etapes_realisees || "aucune"}
- Étapes à venir : ${pv.etapes_a_venir || "aucune"}
- Observations : ${pv.observations || "aucune"}
- Mises en demeure : ${pv.mises_en_demeure || "aucune"}

Rédige un rapport structuré avec les sections suivantes :
1. Objet et contexte
2. Participants
3. État d'avancement
4. Points réalisés
5. Prochaines étapes
6. Observations et points d'attention
7. Mises en demeure (si applicable)
8. Conclusion

Utilise un ton professionnel, formel et précis. Reformule sans inventer d'informations.`;

      const response = await supabase.functions.invoke("generate-gantt", { body: { prompt } });
      const data = response.data;
      const rapport = data.content?.[0]?.text || "";

      await supabase.from("pv_chantier").update({ rapport_ia: rapport }).eq("id", pv.id);
      setPvSelec(p => ({ ...p, rapport_ia: rapport }));
      setMsg("Rapport IA généré ✓");
      fetchPV();
    } catch (e) {
      setMsg("Erreur IA : " + e.message);
    }
    setIaLoading(false);
  }

  async function handleValider(pv) {
    setLoading(true);
    const rapport_final = pvSelec?.rapport_final || pv.rapport_ia;
    await supabase.from("pv_chantier").update({
      statut: "valide", rapport_final,
      valide_par: profile.id, valide_le: new Date().toISOString(),
    }).eq("id", pv.id);

    if (pv.projet_id) {
      const { data: clients } = await supabase.from("profiles").select("id").eq("role", "client").eq("cabinet_id", cabinetId);
      if (clients?.length) {
        await supabase.from("notifications").insert(
          clients.map(c => ({
            cabinet_id: cabinetId,
            destinataire_id: c.id, expediteur_id: profile.id,
            type: "rapport_valide", titre: "Nouveau rapport de chantier disponible",
            message: `Un nouveau rapport de chantier a été validé pour votre projet.`,
            lien_type: "rapport", lien_id: pv.id,
          }))
        );
      }
    }
    setMsg("Rapport validé et envoyé au client ✓");
    fetchPV();
    setTimeout(() => setVue("liste"), 1500);
    setLoading(false);
  }

  async function handleRejeter(pvId) {
    await supabase.from("pv_chantier").update({ statut: "rejete" }).eq("id", pvId);
    setMsg("PV rejeté — l'OPC sera notifié.");
    fetchPV();
    setVue("liste");
  }

  // ── Vue liste ─────────────────────────────────────────────────────────────
  if (vue === "liste") return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1000 }}><div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}><div><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>Chantier — Réunions</div><div style={{ fontSize: 24, fontWeight: 700 }}>PV de Chantier</div><div style={{ fontSize: 13, color: INK_SOFT, marginTop: 3 }}>
            {isAdmin ? "Tous les PV soumis par l'équipe" : "Vos PV de réunion"}
          </div></div>
        {!isAdmin && (
          <button onClick={() => { setVue("nouveau"); setMsg(""); setForm(FORM_INIT); }}
            style={{ padding: "10px 22px", background: GOLD, color: WHITE, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            + Nouveau PV
          </button>
        )}
      </div>

      {pvList.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 48, color: "#bbb", fontStyle: "italic" }}>Aucun PV pour le moment</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {pvList.map(pv => {
            const s = STATUT_COLORS[pv.statut] || STATUT_COLORS.brouillon;
            if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
              <div key={pv.id} onClick={() => { setPvSelec(pv); setVue("detail"); }}
                style={{ ...card, marginBottom: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
              ><div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                    {pv.projet?.name || "Projet inconnu"} — {pv.date_reunion}
                  </div><div style={{ fontSize: 12, color: INK_SOFT }}>
                    {pv.lieu || "—"} · Par {pv.auteur?.full_name || pv.auteur?.email || "—"}
                    {pv.photos?.length > 0 && <span style={{ marginLeft: 8, color: GOLD }}> {pv.photos.length} photo{pv.photos.length > 1 ? "s" : ""}</span>}
                  </div></div><div style={{ display: "flex", alignItems: "center", gap: 12 }}><div style={{ fontSize: 13, fontWeight: 700, color: GOLD }}>{pv.avancement_global}%</div><span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: "4px 12px", fontSize: 11, fontWeight: 700 }}>{s.label}</span><span style={{ color: "#ccc", fontSize: 18 }}>›</span></div></div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Vue nouveau PV ────────────────────────────────────────────────────────
  if (vue === "nouveau") return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 820 }}><div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 14 }}><button onClick={() => setVue("liste")} style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 7, padding: "6px 14px", cursor: "pointer", fontSize: 12, color: INK_SOFT }}>
          ← Retour
        </button><div><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD }}>Nouveau PV</div><div style={{ fontSize: 20, fontWeight: 700 }}>Réunion de chantier</div></div></div>

      {msg && <div style={{ marginBottom: 16, padding: "10px 16px", background: "#E8F6EF", borderRadius: 8, fontSize: 13, color: "#1A7A44", fontWeight: 600 }}>{msg}</div>}

      <div style={card}><div style={sectionTitle}>Informations générales</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}><div><label style={labelStyle}>Projet</label><select style={inputStyle} value={form.projet_id} onChange={e => updateForm("projet_id", e.target.value)}><option value="">Sélectionner un projet</option>
              {projets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div><div><label style={labelStyle}>Date de réunion</label><input style={inputStyle} type="date" value={form.date_reunion} onChange={e => updateForm("date_reunion", e.target.value)} /></div></div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={labelStyle}>Lieu</label><input style={inputStyle} value={form.lieu} onChange={e => updateForm("lieu", e.target.value)} placeholder="Ex : Chantier Villa ARIA" /></div><div><label style={labelStyle}>Personnes présentes</label><input style={inputStyle} value={form.presents} onChange={e => updateForm("presents", e.target.value)} placeholder="Ex : Youssef K., Architecte, Chef chantier…" /></div></div></div><div style={card}><div style={sectionTitle}>Avancement</div><div style={{ marginBottom: 16 }}><label style={labelStyle}>Avancement global : <strong style={{ color: GOLD }}>{form.avancement_global}%</strong></label><input type="range" min="0" max="100" step="5" value={form.avancement_global}
            onChange={e => updateForm("avancement_global", parseInt(e.target.value))}
            style={{ width: "100%", accentColor: GOLD }} /><div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#ccc", marginTop: 2 }}><span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span></div></div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={labelStyle}>Étapes réalisées</label><textarea style={taStyle} value={form.etapes_realisees} onChange={e => updateForm("etapes_realisees", e.target.value)} placeholder="Listez les travaux réalisés depuis la dernière réunion..." /></div><div><label style={labelStyle}>Étapes à venir</label><textarea style={taStyle} value={form.etapes_a_venir} onChange={e => updateForm("etapes_a_venir", e.target.value)} placeholder="Travaux prévus pour la prochaine période..." /></div></div></div><div style={card}><div style={sectionTitle}>Observations & Mises en demeure</div><div style={{ marginBottom: 12 }}><label style={labelStyle}>Observations générales</label><textarea style={taStyle} value={form.observations} onChange={e => updateForm("observations", e.target.value)} placeholder="Points d'attention, problèmes rencontrés, décisions prises..." /></div><div><label style={labelStyle}>Mises en demeure</label><textarea style={{ ...taStyle, borderColor: "#F0DEB4" }} value={form.mises_en_demeure}
            onChange={e => updateForm("mises_en_demeure", e.target.value)}
            placeholder="Indiquez ici les mises en demeure formelles si applicable..." /></div></div>

      {/* ── Photos ── */}
      <div style={card}><div style={sectionTitle}>Photos du chantier</div><label style={{
          display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
          border: `2px dashed ${BORDER}`, borderRadius: 8, cursor: "pointer",
          background: SOFT_BG, marginBottom: 14,
        }}><span style={{ fontSize: 22 }}></span><div><div style={{ fontSize: 13, fontWeight: 600, color: INK }}>Ajouter des photos</div><div style={{ fontSize: 11, color: INK_SOFT }}>JPG, PNG — plusieurs fichiers acceptés</div></div><input type="file" accept="image/*" multiple onChange={handlePhotos} style={{ display: "none" }} /></label>

        {form.photos?.length > 0 && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {form.photos.map((p, i) => (
              <div key={i} style={{ position: "relative" }}><img src={p} alt={`photo-${i}`}
                  style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 8, border: `1px solid ${BORDER}` }} /><button onClick={() => supprimerPhoto(i)}
                  style={{ position: "absolute", top: -6, right: -6, background: "#C0392B", color: WHITE, border: "none", borderRadius: "50%", width: 20, height: 20, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                  ×
                </button></div>
            ))}
          </div>
        )}

        {form.photos?.length === 0 && (
          <div style={{ fontSize: 12, color: "#ccc", fontStyle: "italic", textAlign: "center" }}>
            Aucune photo ajoutée
          </div>
        )}
      </div><div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}><button onClick={() => handleSauvegarder("brouillon")} disabled={loading}
          style={{ padding: "10px 22px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
           Sauvegarder brouillon
        </button><button onClick={() => handleSauvegarder("soumis")} disabled={loading || !form.projet_id || !form.date_reunion}
          style={{ padding: "10px 22px", background: GOLD, color: WHITE, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: !form.projet_id || !form.date_reunion ? 0.5 : 1 }}>
           Soumettre à l'admin
        </button></div></div>
  );

  // ── Vue détail / validation admin ─────────────────────────────────────────
  if (vue === "detail" && pvSelec) {
    const s = STATUT_COLORS[pvSelec.statut] || STATUT_COLORS.brouillon;
    return (
      <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 820 }}><div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 14 }}><button onClick={() => setVue("liste")} style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 7, padding: "6px 14px", cursor: "pointer", fontSize: 12, color: INK_SOFT }}>
            ← Retour
          </button><div style={{ flex: 1 }}><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD }}>{pvSelec.projet?.name}</div><div style={{ fontSize: 20, fontWeight: 700 }}>PV du {pvSelec.date_reunion}</div></div><span style={{ background: s.bg, color: s.color, borderRadius: 999, padding: "5px 14px", fontSize: 12, fontWeight: 700 }}>{s.label}</span></div>

        {msg && <div style={{ marginBottom: 16, padding: "10px 16px", background: "#E8F6EF", borderRadius: 8, fontSize: 13, color: "#1A7A44", fontWeight: 600 }}>{msg}</div>}

        <div style={card}><div style={sectionTitle}>Informations</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 12 }}><div><div style={labelStyle}>Lieu</div><div style={{ fontSize: 13 }}>{pvSelec.lieu || "—"}</div></div><div><div style={labelStyle}>Présents</div><div style={{ fontSize: 13 }}>{pvSelec.presents || "—"}</div></div><div><div style={labelStyle}>Avancement</div><div style={{ fontSize: 22, fontWeight: 800, color: GOLD }}>{pvSelec.avancement_global}%</div></div></div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}><div><div style={labelStyle}>Étapes réalisées</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{pvSelec.etapes_realisees || "—"}</div></div><div><div style={labelStyle}>Étapes à venir</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{pvSelec.etapes_a_venir || "—"}</div></div></div>
          {pvSelec.observations && <div style={{ marginBottom: 8 }}><div style={labelStyle}>Observations</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{pvSelec.observations}</div></div>}
          {pvSelec.mises_en_demeure && (
            <div style={{ background: "#FDF9F2", borderRadius: 8, padding: "12px 14px", border: "1px solid #F0DEB4" }}><div style={{ ...labelStyle, color: GOLD }}>Mises en demeure</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{pvSelec.mises_en_demeure}</div></div>
          )}
        </div>

        {/* Photos du PV */}
        {pvSelec.photos?.length > 0 && (
          <div style={card}><div style={sectionTitle}>Photos du chantier ({pvSelec.photos.length})</div><div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {pvSelec.photos.map((p, i) => (
                <img key={i} src={p} alt={`photo-${i}`}
                  style={{ width: 140, height: 140, objectFit: "cover", borderRadius: 8, border: `1px solid ${BORDER}`, cursor: "pointer" }}
                  onClick={() => window.open(p, "_blank")}
                />
              ))}
            </div></div>
        )}

        {/* Rapport IA */}
        {isAdmin && pvSelec.statut === "soumis" && (
          <div style={card}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}><div style={sectionTitle}>Rapport IA</div><button onClick={() => genererRapportIA(pvSelec)} disabled={iaLoading}
                style={{ padding: "8px 18px", background: INK, color: WHITE, border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {iaLoading ? " Génération..." : " Générer le rapport IA"}
              </button></div>
            {pvSelec.rapport_ia ? (
              <><div style={{ fontSize: 11, color: INK_SOFT, marginBottom: 8, fontStyle: "italic" }}>
                  Rapport généré — vous pouvez le modifier avant validation
                </div><textarea style={{ ...taStyle, minHeight: 320, fontSize: 13, lineHeight: 1.7 }}
                  value={pvSelec.rapport_final ?? pvSelec.rapport_ia}
                  onChange={e => setPvSelec(p => ({ ...p, rapport_final: e.target.value }))}
                /><div style={{ display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" }}><button onClick={() => handleRejeter(pvSelec.id)} disabled={loading}
                    style={{ padding: "9px 20px", background: "#FDEDEC", color: "#C0392B", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    ✗ Rejeter
                  </button><button onClick={() => handleValider(pvSelec)} disabled={loading}
                    style={{ padding: "9px 20px", background: "#1A7A44", color: WHITE, border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    ✓ Valider & Envoyer au client
                  </button></div></>
            ) : (
              <div style={{ padding: "24px 0", textAlign: "center", color: "#bbb", fontSize: 13, fontStyle: "italic" }}>
                Cliquez sur "Générer le rapport IA" pour reformuler automatiquement les notes du PV
              </div>
            )}
          </div>
        )}

        {pvSelec.statut === "valide" && pvSelec.rapport_final && (
          <div style={card}><div style={sectionTitle}>Rapport final validé</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.7, color: INK }}>{pvSelec.rapport_final}</div></div>
        )}
      </div>
    );
  }

  return null;
}